import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import { Me, getUser } from "../../api/users";
import { PostGet } from "../../api/post";
import { getServiceNote, approveServiceNote } from "../../api/service_note";
import { TYLevel } from "../../constants";

const STATUS_TEXT = {
  1: "Не согласовано",
  2: "На доработке",
  3: "Согласовано",
  4: "Отклонено",
};

function statusBadgeClass(statusId, color) {
  if (color === "green") return "bg-success";
  if (color === "yellow") return "bg-warning text-dark";
  if (color === "red") return "bg-danger";
  if (statusId === 3) return "bg-success";
  if (statusId === 2) return "bg-warning text-dark";
  if (statusId === 4) return "bg-danger";
  return "bg-danger";
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTemplateShiftInfo(templateShift) {
  if (!templateShift) return "—";
  const parts = [];
  if (templateShift.date) parts.push(templateShift.date);
  if (templateShift.station_name) parts.push(templateShift.station_name);
  if (templateShift.start != null && templateShift.end != null) {
    parts.push(`${templateShift.start}:00 — ${templateShift.end}:00`);
  }
  if (templateShift.template_shift_id) {
    parts.push(`шаблон № ${templateShift.template_shift_id}`);
  }
  return parts.join(" · ") || "—";
}

function formatEmployeeName(employeeId, snapshotName, employeeNames) {
  if (snapshotName) return snapshotName;
  if (employeeId && employeeNames[employeeId]) return employeeNames[employeeId];
  if (employeeId) return `ID ${employeeId}`;
  return "—";
}

function SummaryField({ label, value }) {
  return (
    <div className="col-md-6">
      <div className="text-muted small">{label}</div>
      <div className="fw-semibold">{value}</div>
    </div>
  );
}

function OperationSummary({ children }) {
  return (
    <div className="service-note-operation-summary mb-3">
      <div className="row g-3">{children}</div>
    </div>
  );
}

function collectEmployeeIds(note) {
  const payload = note?.payload || {};
  const snapshot = payload.snapshot || {};
  const ids = new Set();
  const names = {};

  function addEmployee(employeeId, employeeName) {
    if (!employeeId) return;
    if (employeeName) {
      names[employeeId] = employeeName;
    } else {
      ids.add(employeeId);
    }
  }

  function addShiftPart(part) {
    if (!part) return;
    addEmployee(part.employee_id, part.employee_name);
  }

  switch (note?.operation_type) {
    case "create_shift":
      addEmployee(
        payload.employee_id || snapshot.employee_id,
        snapshot.employee_name,
      );
      break;
    case "delete_shift":
      addShiftPart(snapshot);
      break;
    case "replace_employee":
      addShiftPart(snapshot.shift);
      addEmployee(payload.new_employee_id, snapshot.new_employee_name);
      break;
    case "swap_employees":
      addShiftPart(snapshot.shift_a);
      addShiftPart(snapshot.shift_b);
      break;
    default:
      break;
  }

  return { ids: [...ids], names };
}

function renderOperationSummary(note, employeeNames) {
  const payload = note.payload || {};
  const snapshot = payload.snapshot || {};

  switch (note.operation_type) {
    case "create_shift":
      return (
        <OperationSummary>
          <SummaryField
            label="Сотрудник"
            value={formatEmployeeName(
              payload.employee_id || snapshot.employee_id,
              snapshot.employee_name,
              employeeNames,
            )}
          />
          <SummaryField
            label="Шаблон смены"
            value={formatTemplateShiftInfo(snapshot.template_shift)}
          />
        </OperationSummary>
      );
    case "delete_shift":
      return (
        <OperationSummary>
          <SummaryField
            label="Сотрудник"
            value={formatEmployeeName(
              snapshot.employee_id,
              snapshot.employee_name,
              employeeNames,
            )}
          />
          <SummaryField
            label="Шаблон смены"
            value={formatTemplateShiftInfo(snapshot)}
          />
        </OperationSummary>
      );
    case "replace_employee":
      return (
        <OperationSummary>
          <SummaryField
            label="Текущий сотрудник"
            value={formatEmployeeName(
              snapshot.shift?.employee_id,
              snapshot.shift?.employee_name,
              employeeNames,
            )}
          />
          <SummaryField
            label="Новый сотрудник"
            value={formatEmployeeName(
              payload.new_employee_id,
              snapshot.new_employee_name,
              employeeNames,
            )}
          />
          <SummaryField
            label="Шаблон смены"
            value={formatTemplateShiftInfo(snapshot.shift)}
          />
        </OperationSummary>
      );
    case "swap_employees":
      return (
        <>
          <OperationSummary>
            <SummaryField
              label="Смена 1 — сотрудник"
              value={formatEmployeeName(
                snapshot.shift_a?.employee_id,
                snapshot.shift_a?.employee_name,
                employeeNames,
              )}
            />
            <SummaryField
              label="Смена 1 — шаблон"
              value={formatTemplateShiftInfo(snapshot.shift_a)}
            />
          </OperationSummary>
          <OperationSummary>
            <SummaryField
              label="Смена 2 — сотрудник"
              value={formatEmployeeName(
                snapshot.shift_b?.employee_id,
                snapshot.shift_b?.employee_name,
                employeeNames,
              )}
            />
            <SummaryField
              label="Смена 2 — шаблон"
              value={formatTemplateShiftInfo(snapshot.shift_b)}
            />
          </OperationSummary>
        </>
      );
    default:
      return null;
  }
}

const ServiceNoteDetail = ({ token }) => {
  const { id } = useParams();
  const [note, setNote] = useState(null);
  const [accessLevel, setAccessLevel] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [tyApprove, setTyApprove] = useState({ status_id: "1", TY_comment: "" });
  const [saving, setSaving] = useState(false);
  const [employeeNames, setEmployeeNames] = useState({});

  const isTy = accessLevel >= TYLevel;
  const canApprove = isTy && note && !note.applied_at;
  const hasOperationSummary = [
    "create_shift",
    "delete_shift",
    "replace_employee",
    "swap_employees",
  ].includes(note?.operation_type);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const user = await Me(token);
        const post = await PostGet(token, user.post_id);
        setAccessLevel(post.access_level);

        const data = await getServiceNote(token, id);
        if (data?.detail) {
          setError(data.detail);
          return;
        }
        setNote(data);
        setTyApprove({
          status_id: String(data.status_id ?? 1),
          TY_comment: data.TY_comment || "",
        });
      } catch (err) {
        console.error(err);
        setError("Не удалось загрузить служебную записку");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token, id]);

  useEffect(() => {
    if (!note || !token || !hasOperationSummary) {
      setEmployeeNames({});
      return;
    }

    const { ids, names } = collectEmployeeIds(note);
    if (ids.length === 0) {
      setEmployeeNames(names);
      return;
    }

    let cancelled = false;
    async function loadEmployees() {
      const fetched = { ...names };
      await Promise.all(
        ids.map(async (employeeId) => {
          const user = await getUser(token, employeeId);
          if (user?.detail) return;
          fetched[employeeId] = `${user.surname || ""} ${user.name || ""}`.trim();
        }),
      );
      if (!cancelled) {
        setEmployeeNames(fetched);
      }
    }
    loadEmployees();
    return () => {
      cancelled = true;
    };
  }, [note, token, hasOperationSummary]);

  async function handleApprove() {
    setMessage("");
    setSaving(true);
    try {
      const data = await approveServiceNote(token, id, {
        status_id: Number(tyApprove.status_id),
        TY_comment: tyApprove.TY_comment,
      });
      if (data?.detail) {
        setMessage(data.detail);
        return;
      }
      setNote(data);
      setTyApprove({
        status_id: String(data.status_id ?? 1),
        TY_comment: data.TY_comment || "",
      });
      if (data.applied_at) {
        setMessage("Согласовано и применено к расписанию");
      } else if (Number(tyApprove.status_id) === 3) {
        setMessage(data.detail || "Не удалось применить изменение");
      } else {
        setMessage("Сохранено");
      }
    } catch (err) {
      console.error(err);
      setMessage("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="reports-page container">
        <p className="text-muted">Загрузка…</p>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="reports-page container">
        <p className="text-danger">{error || "Записка не найдена"}</p>
        <Link to="/report-service-notes">← К списку</Link>
      </div>
    );
  }

  return (
    <div className="reports-page container">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/reports">Отчёты</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/report-service-notes">Служебные записки</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            № {note.id}
          </li>
        </ol>
      </nav>

      <h1>Служебная записка № {note.id}</h1>
      <p className="text-muted">
        {note.restaurant_name} · {note.start_date} — {note.end_date} ·{" "}
        {note.operation_type_label || note.operation_type}
      </p>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="text-muted small">Создана</div>
              <div>{formatDateTime(note.created)}</div>
            </div>
            <div className="col-md-4">
              <div className="text-muted small">Директор</div>
              <div>{note.creater_name}</div>
            </div>
            <div className="col-md-4">
              <div className="text-muted small">Применено</div>
              <div>
                {note.applied_at ? (
                  <span className="badge bg-success">
                    {formatDateTime(note.applied_at)}
                  </span>
                ) : note.apply_error ? (
                  <span className="badge bg-danger" title={note.apply_error}>
                    Ошибка
                  </span>
                ) : (
                  "Нет"
                )}
              </div>
            </div>
          </div>

          <table className="table table-bordered schedule-chk-table mb-0">
            <thead>
              <tr className="text-center">
                <th>Директор</th>
                <th>Территориальный управляющий</th>
              </tr>
            </thead>
            <tbody>
              <tr className="text-center">
                <td>
                  <span className="badge bg-secondary">Автор записки</span>
                </td>
                <td>
                  <span
                    className={`badge ${statusBadgeClass(note.status_id, note.status_color)}`}
                  >
                    {note.status_name || STATUS_TEXT[note.status_id] || "—"}
                  </span>
                </td>
              </tr>
              <tr className="text-center">
                <td>{note.creater_name}</td>
                <td>{note.TY_surname_name || "—"}</td>
              </tr>
              <tr>
                <td>
                  <label className="form-label small text-muted mb-1">
                    Комментарий директора
                  </label>
                  <div className="service-note-comment-box">
                    {note.comment || "—"}
                  </div>
                </td>
                <td>
                  <label className="form-label small text-muted mb-1">
                    Комментарий ТУ
                  </label>
                  {canApprove ? (
                    <textarea
                      className="form-control"
                      rows={4}
                      value={tyApprove.TY_comment}
                      onChange={(e) =>
                        setTyApprove({ ...tyApprove, TY_comment: e.target.value })
                      }
                    />
                  ) : (
                    <div className="service-note-comment-box">
                      {note.TY_comment || "—"}
                    </div>
                  )}
                </td>
              </tr>
              {canApprove && (
                <tr>
                  <td />
                  <td>
                    <div className="mb-3">
                      <label className="form-label">Этап согласования</label>
                      <select
                        className="form-select"
                        value={tyApprove.status_id}
                        onChange={(e) =>
                          setTyApprove({ ...tyApprove, status_id: e.target.value })
                        }
                      >
                        <option value="1">Не согласовано</option>
                        <option value="2">На доработке</option>
                        <option value="3">Согласовано</option>
                        <option value="4">Отклонено</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      className="btn w-100"
                      onClick={handleApprove}
                      disabled={saving}
                    >
                      {saving ? "Сохранение…" : "Сохранить согласование"}
                    </button>
                    {message && (
                      <p
                        className={`small mt-2 mb-0 ${
                          message.includes("применено") || message === "Сохранено"
                            ? "text-success"
                            : "text-danger"
                        }`}
                      >
                        {message}
                      </p>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body">
          <h2 className="h5 mb-3">Операция</h2>
          <p className="mb-2">
            <strong>Тип:</strong> {note.operation_type_label || note.operation_type}
          </p>
          {hasOperationSummary &&
            renderOperationSummary(note, employeeNames)}
          {note.apply_error && (
            <p className="text-danger small mb-2">
              <strong>Ошибка применения:</strong> {note.apply_error}
            </p>
          )}
          {note.operation?.result && (
            <div className="mb-3">
              <div className="text-muted small mb-2">Результат применения</div>
              <pre className="service-note-snapshot mb-0">
                {JSON.stringify(note.operation.result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceNoteDetail;
